import { Injectable, inject } from '@angular/core';
import {
  ConfigPackLoader,
  WorkflowModuleBridge,
  WorkflowRunStore,
  type RunState,
} from '@los-workspace/workflow-runtime';
import { coerceText } from '../utils/coerce-text';
import {
  buildExistingCustomerSearchQueryParams,
  buildFetchExistingCustomerFromQueryParams,
} from '../components/workflow-pack-form/fetch-existing-customer.mapper';
import {
  edgeConditionValue,
  isNewCustomerCreateFormNodeKey,
  normalizeStatusIntent,
  normalizeTypeIntent,
  opportunityActionIntent,
  pickChoiceEdge,
} from '../utils/opportunity-workflow-nodes';
import { OpportunityNavigationState, type OpportunityView, type OpportunityViewParams } from './opportunity-navigation.state';
import { NgxIndexedDbWorkflowRunStorageAdapter } from '../../../core/indexed-db/ngx-indexed-db-workflow-run-storage.adapter';
import {
  OpportunityShellPersistenceService,
  type PersistedShellNav,
} from './opportunity-shell-persistence.service';
import { OpportunityWorkflowConfigService } from './opportunity-workflow-config.service';
import {
  logOpportunityNavTransition,
} from '../utils/opportunity-workflow-debug.util';

function contextApplicantType(ctx: Record<string, unknown>): 'corporate' | 'individual' {
  return normalizeTypeIntent(
    coerceText(ctx['applicantType'] ?? ctx['applicantTypes'] ?? ''),
  );
}

function resolveCreateApplicantType(ctx: Record<string, unknown>): 'corporate' | 'individual' {
  return contextApplicantType(ctx);
}

function resolveCreateApplicantStatus(
  ctx: Record<string, unknown>,
  wfStep?: string | null,
): 'existing' | 'new' {
  const step = coerceText(wfStep ?? '').trim();
  if (step && isNewCustomerCreateFormNodeKey(step)) {
    return 'new';
  }
  return normalizeStatusIntent(
    coerceText(ctx['applicantStatus'] ?? ctx['statusOptions'] ?? ctx['customerStatus'] ?? ''),
  );
}

function readSelectedRow(ctx: Record<string, unknown>): Record<string, unknown> | undefined {
  const row = ctx['selectedRow'];
  return row && typeof row === 'object' ? (row as Record<string, unknown>) : undefined;
}

function optionalQueryText(value: unknown): string | undefined {
  const text = coerceText(value);
  if (!text) {
    return undefined;
  }
  return text;
}

function queryTextIfPresent(value: unknown): string | undefined {
  return value != null ? optionalQueryText(value) : undefined;
}

function buildSelectedRowQueryParams(
  row: Record<string, unknown> | undefined,
): Record<string, string | undefined> {
  if (!row) {
    return {};
  }

  return {
    contact: optionalQueryText(row['mobile']),
    emailId: optionalQueryText(row['emailId']),
    genderCode: optionalQueryText(row['genderCode']),
    dob: optionalQueryText(row['dob']),
    dateOfIncorporation: optionalQueryText(row['dateOfIncorporation']),
    pan: optionalQueryText(row['pan']),
    address: optionalQueryText(row['address']),
    addressLine1: optionalQueryText(row['addressLine1']),
    addressLine2: optionalQueryText(row['addressLine2']),
    pinCode: optionalQueryText(row['pinCode']),
    city: optionalQueryText(row['city']),
    district: optionalQueryText(row['district']),
    stateCode: optionalQueryText(row['stateCode']),
    countryCode: optionalQueryText(row['countryCode']),
    addressTypeCode: optionalQueryText(row['addressTypeCode']),
  };
}

function buildCreateFormViewParams(
  ctx: Record<string, unknown>,
  extra: Partial<OpportunityViewParams> = {},
): OpportunityViewParams {
  const selectedRow = readSelectedRow(ctx);
  const wfStep = coerceText(extra.wfStep ?? '').trim();
  const applicantType = resolveCreateApplicantType(ctx);
  const applicantStatus = resolveCreateApplicantStatus(ctx, wfStep);
  const customerId =
    applicantStatus === 'existing'
      ? queryTextIfPresent(
          ctx['customerId'] ?? selectedRow?.['customerId'] ?? selectedRow?.['column7'],
        )
      : queryTextIfPresent(ctx['customerId']);
  const partyId =
    applicantStatus === 'existing'
      ? queryTextIfPresent(
          ctx['partyId'] ?? ctx['id'] ?? selectedRow?.['id'] ?? customerId,
        )
      : queryTextIfPresent(ctx['partyId']);
  const { id: _id, opportunityId: _opportunityId, ...createExtra } = extra;

  return {
    applicantType,
    applicantStatus,
    customerId,
    customerName: queryTextIfPresent(
      ctx['customerName'] ?? selectedRow?.['name'] ?? selectedRow?.['customerName'],
    ),
    partyId,
    ...buildSelectedRowQueryParams(selectedRow),
    ...createExtra,
  };
}

function buildEditFormViewParams(
  ctx: Record<string, unknown>,
  wfConfig: OpportunityWorkflowConfigService,
  nodeKey: string,
  extra: Partial<OpportunityViewParams> = {},
): OpportunityViewParams | null {
  const opportunityId =
    wfConfig.readOpportunityIdFromContext(ctx) ||
    coerceText(extra.opportunityId ?? extra.id ?? '').trim();
  if (!opportunityId) {
    return null;
  }
  const { id: _id, opportunityId: _oid, partyId: _partyId, ...editExtra } = extra;
  return {
    id: opportunityId,
    opportunityId,
    applicantType: normalizeTypeIntent(coerceText(ctx['applicantType'] ?? extra.applicantType ?? '')),
    wfStep: nodeKey,
    ...editExtra,
  };
}

function buildDetailViewParams(
  ctx: Record<string, unknown>,
  wfConfig: OpportunityWorkflowConfigService,
  nodeKey: string,
  extra: Partial<OpportunityViewParams> = {},
): OpportunityViewParams | null {
  const opportunityId =
    wfConfig.readOpportunityIdFromContext(ctx) ||
    coerceText(extra.opportunityId ?? extra.id ?? '').trim();
  if (!opportunityId) {
    return null;
  }
  const {
    id: _id,
    opportunityId: _oid,
    partyId: _partyId,
    customerId: _customerId,
    applicantStatus: _status,
    ...detailExtra
  } = extra;
  return {
    opportunityId,
    id: opportunityId,
    applicantType: normalizeTypeIntent(coerceText(ctx['applicantType'] ?? '')),
    wfStep: nodeKey,
    detailTab: extra.detailTab ?? 'lead-details',
    action: extra.action,
    ...detailExtra,
  };
}

function buildCustomerSearchViewParams(
  ctx: Record<string, unknown>,
  extra: Partial<OpportunityViewParams> = {},
): OpportunityViewParams {
  const {
    id: _id,
    opportunityId: _opportunityId,
    partyId: _partyId,
    ...searchExtra
  } = extra;
  const stringParams = Object.fromEntries(
    Object.entries({ ...ctx, ...searchExtra }).map(([key, value]) => [key, coerceText(value)]),
  );
  const request = buildFetchExistingCustomerFromQueryParams(stringParams);
  return {
    applicantType: resolveCreateApplicantType(ctx),
    applicantStatus: 'existing',
    ...buildExistingCustomerSearchQueryParams(request, 'existing'),
    ...searchExtra,
  } as OpportunityViewParams;
}

function viewParamsFromQuery(extraQuery: Record<string, string>): Partial<OpportunityViewParams> {
  return { ...extraQuery };
}

/**
 * Bridges the OPPORTUNITY workflow engine to the existing admin Opportunities
 * pages (list / create / details / customer search) — no generic wf-* panels.
 */
@Injectable({ providedIn: 'root' })
export class OpportunityWorkflowBridge extends WorkflowModuleBridge {
  private readonly store = inject(WorkflowRunStore);
  private readonly pack = inject(ConfigPackLoader);
  private readonly wfConfig = inject(OpportunityWorkflowConfigService);
  private readonly nav = inject(OpportunityNavigationState);
  private readonly shellPersistence = inject(OpportunityShellPersistenceService);
  private readonly workflowRunStorage = inject(NgxIndexedDbWorkflowRunStorageAdapter);

  readonly state = this.store.state;
  readonly loading = this.store.loading;

  /**
   * Set to true by leaveModule() when the user explicitly navigates away via
   * the sidebar.  Checked (and reset) by restoreShellSession() so the next
   * shell entry always lands on the list page rather than restoring the last
   * detail/create view.  Using a singleton-service flag avoids the IndexedDB
   * race between leaveModule()'s async clear and the new shell's bootstrap.
   */
  private moduleLeftIntentionally = false;

  private async workflowKey(): Promise<string> {
    await this.pack.load();
    return this.pack.resolveWorkflowKey('OPPORTUNITY');
  }

  async ensureRunning(): Promise<RunState> {
    await this.pack.load();
    const key = await this.workflowKey();
    const current = this.store.state();
    if (
      current?.workflowKey?.toUpperCase() === key.toUpperCase() &&
      (current.status === 'paused' || current.status === 'running')
    ) {
      return current;
    }

    // Prefer a prior persisted instance if still at the opportunity list.
    try {
      const activeId = await this.store.readActiveInstanceId(key);
      if (activeId) {
        const hydrated = await this.store.hydrate(activeId);
        if (hydrated && hydrated.status === 'paused') return hydrated;
      }

      const instanceIds = await this.store.listStoredInstanceIds(key);
      for (const instanceId of instanceIds) {
        const hydrated = await this.store.hydrate(instanceId);
        if (hydrated && hydrated.status === 'paused') return hydrated;
      }
    } catch {
      // ignore
    }

    return this.store.start(key);
  }

  currentNodeKey(): string | undefined {
    return this.store.state()?.currentNodeKey;
  }

  isPausedAt(nodeKey: string): boolean {
    const s = this.store.state();
    return s?.status === 'paused' && s.currentNodeKey === nodeKey;
  }

  isChoicePause(): boolean {
    const key = this.currentNodeKey();
    return this.wfConfig.isApplicantTypeNode(key) || !!this.wfConfig.isStatusNode(key);
  }

  isSearchFormPause(): boolean {
    return this.wfConfig.isSearchNode(this.currentNodeKey());
  }

  isCreateFormPause(): boolean {
    const key = this.currentNodeKey() ?? '';
    return this.wfConfig.isCreateNode(key) || key.startsWith('form_create_');
  }

  isCustomerDatasetPause(): boolean {
    return this.wfConfig.isCustomerDatasetNode(this.currentNodeKey());
  }

  isDetailPause(): boolean {
    return this.wfConfig.isViewNode(this.currentNodeKey());
  }

  isListPause(): boolean {
    return this.wfConfig.isListNode(this.currentNodeKey());
  }

  /** True when paused anywhere in the create-new / create-existing flow. */
  isCreateFlowPause(): boolean {
    const key = this.currentNodeKey();
    if (!key) {
      return false;
    }
    return (
      this.wfConfig.isCreateModalNode(key) ||
      this.wfConfig.isCustomerDatasetNode(key) ||
      this.wfConfig.isCreateFormPauseNode(key)
    );
  }

  /** Wipe Opportunity kv aux keys, drafts, nav, and persisted workflow runs. */
  async clearPersistedOpportunityModule(): Promise<void> {
    await this.shellPersistence.clearAllOpportunitySession();
    try {
      await this.pack.load();
      const key = await this.workflowKey();
      await this.workflowRunStorage.clearWorkflow(key);
    } catch {
      // ignore when pack/workflow key unavailable
    }
    this.store.state.set(null);
  }

  /** Public alias for shell bootstrap and auth guard. */
  async resetModulePersistence(): Promise<void> {
    await this.clearPersistedOpportunityModule();
  }

  /**
   * In-module return to list (not create ↔ bureau): clear persisted session and pause at list.
   */
  async resetKvForListEntry(): Promise<void> {
    await this.clearPersistedOpportunityModule();
    await this.wfConfig.ensureLoaded();
    await this.ensureAtList();
  }

  /**
   * Top-level module entry reset (sidebar OPPORTUNITY, external nav to /opportunities).
   * Restarts or restores the workflow at the list node and clears in-shell sub-views.
   */
  async returnToModuleList(
    extraParams: Partial<OpportunityViewParams> = {},
  ): Promise<void> {
    await this.clearPersistedOpportunityModule();
    await this.wfConfig.ensureLoaded();
    await this.ensureAtList();
    this.nav.replaceView('list', extraParams as OpportunityViewParams);
  }

  /** Clear shell navigation + drafts when leaving the Opportunities module (sidebar / route change). */
  async leaveModule(): Promise<void> {
    // Signal to the next bootstrap that this was an intentional sidebar navigation
    // away — restoreShellSession() will skip restoration and go to the list page.
    this.moduleLeftIntentionally = true;
    this.nav.replaceView('list', {});
    await this.clearPersistedOpportunityModule();
  }

  /**
   * Hydrate a paused OPPORTUNITY run from IndexedDB without starting a new run.
   * When {@link persistedNav} is provided, prefer the paused run that matches the
   * saved shell view over a stale list-pointer run.
   */
  async tryHydratePausedRun(
    persistedNav?: PersistedShellNav | null,
  ): Promise<RunState | null> {
    await this.pack.load();
    const key = await this.workflowKey();
    const current = this.store.state();
    if (
      current?.workflowKey?.toUpperCase() === key.toUpperCase() &&
      current.status === 'paused' &&
      current.currentNodeKey
    ) {
      return current;
    }

    await this.wfConfig.ensureLoaded();
    const activeId = await this.store.readActiveInstanceId(key);
    const candidates = await this.collectPausedRunCandidates(key, activeId);
    if (!candidates.length) {
      return null;
    }

    const selected = this.selectPausedRunCandidate(candidates, persistedNav, activeId);
    if (!selected) {
      return null;
    }

    // syncView() reads store.state() — re-hydrate the chosen run so state matches selection.
    await this.store.hydrate(selected.instanceId);
    return this.store.state();
  }

  private async collectPausedRunCandidates(
    workflowKey: string,
    activeId: string | null,
  ): Promise<RunState[]> {
    const candidates: RunState[] = [];
    const seen = new Set<string>();
    const instanceIds = [
      ...(activeId ? [activeId] : []),
      ...(await this.store.listStoredInstanceIds(workflowKey)),
    ];

    for (const instanceId of instanceIds) {
      if (seen.has(instanceId)) {
        continue;
      }
      seen.add(instanceId);
      try {
        const hydrated = await this.store.hydrate(instanceId);
        if (hydrated?.status === 'paused' && hydrated.currentNodeKey) {
          candidates.push(hydrated);
        }
      } catch {
        // skip corrupt snapshot
      }
    }

    return candidates;
  }

  private selectPausedRunCandidate(
    candidates: RunState[],
    persistedNav: PersistedShellNav | null | undefined,
    activeId: string | null,
  ): RunState | null {
    let selected: RunState | null = candidates[0] ?? null;

    if (persistedNav && this.isPersistedEditSession(persistedNav)) {
      return this.selectEditSessionPausedRun(candidates, persistedNav);
    }

    if (persistedNav && persistedNav.view !== 'list') {
      selected = this.selectNonListPausedRun(candidates, persistedNav, selected);
    } else if (activeId) {
      selected = candidates.find((candidate) => candidate.instanceId === activeId) ?? selected;
    }

    return selected;
  }

  private selectEditSessionPausedRun(
    candidates: RunState[],
    persistedNav: PersistedShellNav,
  ): RunState | null {
    const editId = coerceText(
      persistedNav.params.id ?? persistedNav.params.opportunityId ?? '',
    ).trim();
    const editWfStep = persistedNav.params.wfStep?.trim() ?? '';
    const matchEditRun = (strictStep: boolean): RunState | undefined =>
      candidates.find((candidate) => {
        const nodeKey = candidate.currentNodeKey ?? '';
        if (!this.wfConfig.isEditFormPauseNode(nodeKey)) {
          return false;
        }
        if (strictStep && editWfStep && nodeKey !== editWfStep) {
          return false;
        }
        const ctxId = this.wfConfig.readOpportunityIdFromContext(candidate.contextSoFar);
        return !ctxId || ctxId === editId;
      });

    return matchEditRun(true) ?? matchEditRun(false) ?? null;
  }

  private selectNonListPausedRun(
    candidates: RunState[],
    persistedNav: PersistedShellNav,
    fallback: RunState | null,
  ): RunState | null {
    const matched = candidates.find((candidate) => {
      const nodeKey = candidate.currentNodeKey ?? '';
      const resolution = this.wfConfig.shellViewForPausedNode(
        nodeKey,
        candidate.contextSoFar,
        persistedNav.params,
      );
      if (resolution?.view !== persistedNav.view) {
        return false;
      }
      if (this.isPersistedCreateSession(persistedNav)) {
        return this.wfConfig.isCreateFormPauseNode(nodeKey);
      }
      return true;
    });
    if (matched) {
      return matched;
    }

    return (
      candidates.find((candidate) => !this.wfConfig.isListNode(candidate.currentNodeKey)) ??
      fallback
    );
  }

  /**
   * Restore Opportunities shell view after browser refresh using persisted workflow + nav hints.
   *
   * Returns false when the user explicitly left via the sidebar (leaveModule set the
   * moduleLeftIntentionally flag), causing bootstrapShell to fall through to
   * returnToModuleList() and always land on the list page.
   */
  async restoreShellSession(): Promise<boolean> {
    // If the user navigated away intentionally (sidebar click), always start fresh
    // at the list page regardless of what IndexedDB may still contain.
    if (this.moduleLeftIntentionally) {
      this.moduleLeftIntentionally = false;
      return false;
    }

    await this.wfConfig.ensureLoaded();
    const persisted = await this.shellPersistence.loadNavigationState();

    if (persisted?.view === 'credit-bureau') {
      return this.restoreCreditBureauSession(persisted.params);
    }

    const restoreParams = this.buildRestoreParams(persisted);

    if (persisted && this.isPersistedEditSession(persisted)) {
      return this.restorePersistedEditSession(persisted);
    }

    const run = await this.tryHydratePausedRun(persisted);
    if (run?.status === 'paused' && run.currentNodeKey) {
      await this.syncView(restoreParams);
      return true;
    }

    if (persisted && persisted.view !== 'list') {
      return this.restorePersistedNonListSession(persisted, restoreParams);
    }

    return false;
  }

  private restoreCreditBureauSession(
    params: OpportunityViewParams,
  ): boolean {
    if (!this.isRestorableCreditBureauSession(params)) {
      return false;
    }
    this.nav.replaceView('credit-bureau', params);
    return true;
  }

  private async restorePersistedEditSession(
    persisted: PersistedShellNav,
  ): Promise<boolean> {
    this.nav.replaceView(persisted.view, persisted.params);
    const editRun = await this.tryHydratePausedRun(persisted);
    if (
      editRun?.status === 'paused' &&
      editRun.currentNodeKey &&
      this.wfConfig.isEditFormPauseNode(editRun.currentNodeKey)
    ) {
      await this.store.hydrate(editRun.instanceId);
    }
    return true;
  }

  private async restorePersistedNonListSession(
    persisted: PersistedShellNav,
    restoreParams: Partial<OpportunityViewParams>,
  ): Promise<boolean> {
    this.nav.replaceView(persisted.view, persisted.params);
    const recovered = await this.tryHydratePausedRun(null);
    if (recovered?.status === 'paused' && recovered.currentNodeKey) {
      await this.syncView(restoreParams);
    }
    return true;
  }

  private buildRestoreParams(
    persisted: PersistedShellNav | null,
  ): Partial<OpportunityViewParams> {
    if (!persisted?.params) {
      return {};
    }
    return { ...persisted.params };
  }

  private isPersistedEditSession(persisted: PersistedShellNav): boolean {
    const wfStep = persisted.params.wfStep?.trim();
    const editId = coerceText(
      persisted.params.id ?? persisted.params.opportunityId ?? '',
    ).trim();
    return (
      persisted.view === 'create' &&
      !!editId &&
      !!wfStep &&
      this.wfConfig.isEditFormPauseNode(wfStep)
    );
  }

  private isPersistedCreateSession(persisted: PersistedShellNav): boolean {
    const wfStep = persisted.params.wfStep?.trim();
    if (persisted.view !== 'create') {
      return false;
    }
    if (
      coerceText(persisted.params.id ?? persisted.params.opportunityId ?? '').trim()
    ) {
      return false;
    }
    return !wfStep || this.wfConfig.isCreateFormPauseNode(wfStep);
  }

  private navHasEditIntent(params: OpportunityViewParams): boolean {
    const wfStep = params.wfStep?.trim();
    const editId = coerceText(params.id ?? params.opportunityId ?? '').trim();
    return (
      !!editId &&
      !!wfStep &&
      this.wfConfig.isEditFormPauseNode(wfStep)
    );
  }

  /**
   * Ensure the OPPORTUNITY run is paused on the list dataset.
   * Used when browsing the list (sidebar/nav) so abandoned create pauses
   * do not reopen the Create modal.
   */
  async ensureAtList(): Promise<RunState> {
    await this.wfConfig.ensureLoaded();
    let s = await this.ensureRunning();
    if (this.wfConfig.isListNode(s.currentNodeKey)) return s;

    if (s.status === 'completed' || s.status === 'failed' || s.currentNodeType === 'END') {
      return this.store.start(await this.workflowKey());
    }

    if (!this.wfConfig.isListNode(s.currentNodeKey)) {
      return this.store.start(await this.workflowKey());
    }
    return s;
  }

  /** Toolbar "+ Create New Opportunity" from the list page. */
  async beginCreate(): Promise<RunState> {
    await this.wfConfig.ensureLoaded();
    const wfKey = await this.workflowKey();
    // Always start a fresh run so a prior list Edit/View pause cannot leak into create.
    this.shellPersistence.clearCreateDrafts();
    await this.store.start(wfKey);
    const createKey =
      this.wfConfig.dataset()?.actions.find((action) =>
        action.label.toLowerCase().includes('create'),
      )?.key ?? 'create_opportunity';
    const sourceHandle = this.wfConfig.listActionHandle(createKey);
    const edge = this.wfConfig
      .outgoingEdges(this.wfConfig.listNodeKey())
      .find((item) => item.sourceHandle === sourceHandle);
    return this.store.resume({
      edgeId: edge?.id,
      sourceHandle,
      output: { actionKey: createKey, ...this.clearOpportunityRecordContext() },
    });
  }

  /** Strip prior list/view/edit ids from workflow payload when starting create. */
  private clearOpportunityRecordContext(): Record<string, unknown> {
    const ifElseField = this.wfConfig.ifElseApplicantFieldCode();
    return {
      opportunityId: '',
      opportunityID: '',
      id: '',
      partyId: '',
      customerId: '',
      selectedRow: null,
      actionKey: 'create_opportunity',
      applicantType: '',
      applicantTypes: '',
      applicantStatus: '',
      statusOptions: '',
      customerStatus: '',
      ...(ifElseField ? { [ifElseField]: '' } : {}),
    };
  }

  /**
   * Resume the workflow from dataset_opportunity for any row/toolbar action
   * declared in the active workflow JSON (`dataset_opportunity.actions`).
   */
  async executeListAction(
    actionKey: string,
    output: Record<string, unknown>,
  ): Promise<RunState> {
    await this.ensureAtList();
    await this.wfConfig.ensureLoaded();
    const sourceHandle = this.wfConfig.listActionHandle(actionKey);
    const edge = this.wfConfig
      .outgoingEdges(this.wfConfig.listNodeKey())
      .find((item) => item.sourceHandle === sourceHandle);
    const state = await this.store.resume({
      edgeId: edge?.id,
      sourceHandle,
      output: { actionKey, ...output },
    });
    return state;
  }

  /** List row → view details (workflow-driven). */
  async viewDetails(row: Record<string, unknown>): Promise<RunState> {
    await this.wfConfig.ensureLoaded();
    const actions = this.wfConfig.listRowActions();
    const viewAction =
      actions.find((action) => opportunityActionIntent(action.key, actions) === 'view') ??
      actions.find((action) => action.key === 'view_details');
    const actionKey = viewAction?.key ?? 'view_details';
    const id = coerceText(row['id'] ?? row['opportunityId'] ?? '');
    return this.executeListAction(actionKey, {
      ...this.wfConfig.buildListActionOutput(actionKey, {
        ...row,
        id: id || row['id'],
        opportunityId: id || row['opportunityId'],
      }),
    });
  }

  /** Detail page or list → edit form (workflow-driven). */
  async openEdit(row: Record<string, unknown>): Promise<RunState> {
    await this.wfConfig.ensureLoaded();
    const actions = this.wfConfig.listRowActions();
    const editAction = actions.find(
      (action) => opportunityActionIntent(action.key, actions) === 'edit',
    );
    const actionKey = editAction?.key ?? 'edit';
    return this.executeListAction(
      actionKey,
      this.wfConfig.buildListActionOutput(actionKey, row),
    );
  }

  /** Edit form back → detail view (workflow-driven). */
  async returnToDetail(
    row: Record<string, unknown>,
    extraParams: Partial<OpportunityViewParams> = {},
  ): Promise<void> {
    await this.viewDetails(row);
    await this.syncView(extraParams);
  }

  /**
   * Credit bureau is not a workflow pause node — open as an auxiliary shell view
   * while preserving the current workflow run.
   */
  openCreditBureau(params: Partial<OpportunityViewParams>): void {
    const s = this.store.state();
    const ctx = s?.contextSoFar ?? {};
    const current = this.nav.params();
    // Capture where we are NOW so closeCreditBureau can return there deterministically.
    const originView = this.nav.view();
    const originReturnView: 'create' | 'detail' | undefined =
      originView === 'create' || originView === 'detail' ? originView : undefined;

    const bureauParams: OpportunityViewParams = {
      applicantType:
        params.applicantType ??
        current.applicantType ??
        coerceText(ctx['applicantType']),
      applicantStatus:
        params.applicantStatus ??
        current.applicantStatus ??
        coerceText(ctx['applicantStatus']),
      opportunityId:
        params.opportunityId ??
        current.opportunityId ??
        coerceText(ctx['opportunityId'] ?? ctx['id']),
      id: params.id ?? current.id ?? coerceText(ctx['id']),
      partyId: params.partyId ?? current.partyId,
      customerId: params.customerId ?? current.customerId,
      customerName: params.customerName ?? current.customerName,
      returnTo:
        params.returnTo ??
        current.returnTo ??
        coerceText(ctx['opportunityId'] ?? ctx['id']),
      idProofType: params.idProofType ?? current.idProofType,
      idProofNumber: params.idProofNumber ?? current.idProofNumber,
      wfStep: params.wfStep ?? current.wfStep,
      returnView: params.returnView ?? originReturnView,
    };

    const explicitMode = coerceText(params.mode);
    if (explicitMode) {
      bureauParams.mode = explicitMode;
    }

    const explicitPullId = coerceText(params.bureauReportId);
    if (explicitPullId) {
      bureauParams.bureauReportId = explicitPullId;
    }

    const explicitBureauScore = coerceText(params.bureauScore);
    if (explicitBureauScore) {
      bureauParams.bureauScore = explicitBureauScore;
    }

    this.nav.replaceView('credit-bureau', bureauParams);
  }

  /** Skip restoring a half-finished bureau session (no pull id and no ID proof). */
  private isRestorableCreditBureauSession(params: OpportunityViewParams): boolean {
    const pullId = coerceText(params.bureauReportId);
    const idProof = coerceText(params.idProofNumber);
    return Boolean(pullId || idProof);
  }

  /**
   * Leave Credit Bureau and return to the originating view.
   *
   * Priority:
   *   1. If params.returnView is set, navigate directly (no workflow sync needed).
   *   2. Otherwise fall back to syncView() (detail / underwriter flows).
   */
  async closeCreditBureau(
    extraParams: Partial<OpportunityViewParams> = {},
  ): Promise<void> {
    const navParams = this.nav.params();
    const returnView = navParams.returnView;

    if (returnView === 'detail') {
      const opportunityId = coerceText(
        navParams.returnTo ?? navParams.opportunityId ?? navParams.id,
      ).trim();
      if (opportunityId) {
        const detailParams: OpportunityViewParams = {
          opportunityId,
          id: opportunityId,
          bureauScore: extraParams.bureauScore ?? navParams.bureauScore,
          bureauReportId:
            extraParams.bureauReportId ?? navParams.bureauReportId,
        };
        this.nav.replaceView('detail', detailParams);
        return;
      }
    }

    if (returnView === 'create') {
      // Return to the create/edit form preserving ALL routing context so the
      // component re-enters the correct mode (edit vs. new, existing vs. new customer).
      const createParams: OpportunityViewParams = {
        applicantType: navParams.applicantType,
        applicantStatus: navParams.applicantStatus,
        opportunityId: navParams.opportunityId ?? navParams.id,
        id: navParams.id ?? navParams.opportunityId,
        partyId: navParams.partyId,
        customerId: navParams.customerId,
        customerName: navParams.customerName,
        wfStep: navParams.wfStep,
        bureauScore: extraParams.bureauScore ?? navParams.bureauScore,
        bureauReportId: extraParams.bureauReportId ?? navParams.bureauReportId,
      };
      this.nav.replaceView('create', createParams);
      return;
    }

    // Legacy / underwriter flows: let the workflow drive the navigation.
    await this.syncView(extraParams);
  }

  /** Re-sync shell view from workflow after a navigation error. */
  async recoverView(extraParams: Partial<OpportunityViewParams> = {}): Promise<void> {
    await this.ensureRunning();
    await this.syncView(extraParams);
  }

  /**
   * After draft form save — advance workflow then return to the opportunity list.
   * @param listTab `draft` after Save Draft; `all` after submitting a draft via Save Changes.
   */
  async afterDraftSaved(
    opportunityId: string,
    listTab: 'all' | 'draft' = 'draft',
  ): Promise<void> {
    try {
      await this.afterCreateSaved({ id: opportunityId, opportunityId });
    } catch {
      await this.backToList();
    }
    await this.syncView({ tab: listTab, id: opportunityId });
  }

  /**
   * After edit form submit succeeds — advance workflow (skips duplicate POST).
   */
  async afterEditSaved(detail: { id: string; [key: string]: unknown }): Promise<RunState> {
    const s = this.store.state();
    if (!s || s.status !== 'paused' || !this.wfConfig.isEditNode(s.currentNodeKey)) {
      await this.backToList();
      return this.store.state()!;
    }
    return this.store.resume({
      output: {
        ...detail,
        opportunityId: detail.id,
        id: detail.id,
        formSubmitted: true,
      },
    });
  }

  /** After detail action (reject, etc.) completes — return to list via workflow. */
  async afterDetailActionCompleted(
    extraParams: Partial<OpportunityViewParams> = {},
  ): Promise<void> {
    await this.backToList();
    await this.syncView(extraParams);
  }

  async chooseApplicantType(uiType: string): Promise<RunState> {
    await this.wfConfig.ensureLoaded();
    const intent = normalizeTypeIntent(uiType);
    const s = this.store.state();
    if (
      !s ||
      s.status !== 'paused' ||
      !this.wfConfig.isApplicantTypeNode(s.currentNodeKey)
    ) {
      throw new Error(
        `Workflow not at applicant type choice (paused at ${s?.currentNodeKey ?? 'none'})`,
      );
    }
    const edges = s.availableEdges ?? [];
    const outgoing = this.wfConfig.outgoingEdges(s.currentNodeKey);
    const edge = pickChoiceEdge(edges, intent, (edgeId) => {
      const target = outgoing.find((item) => item.id === edgeId)?.targetNodeKey;
      return this.wfConfig.isStatusNode(target) === intent;
    });
    const value = edge ? edgeConditionValue(edge) || uiType : uiType;
    const fieldCode = this.wfConfig.choice()?.fieldCode ?? 'applicantType';
    return this.store.resume({
      edgeId: edge?.id,
      output: {
        ...this.clearOpportunityRecordContext(),
        [fieldCode]: value,
        applicantType: intent,
        applicantTypes: value,
      },
    });
  }

  async chooseCustomerStatus(uiStatus: string): Promise<RunState> {
    await this.wfConfig.ensureLoaded();
    const intent = normalizeStatusIntent(uiStatus);
    const s = this.store.state();
    if (!s || s.status !== 'paused' || !this.wfConfig.isStatusNode(s.currentNodeKey)) {
      throw new Error(
        `Workflow not at applicant status choice (paused at ${s?.currentNodeKey ?? 'none'})`,
      );
    }
    const edges = s.availableEdges ?? [];
    const outgoing = this.wfConfig.outgoingEdges(s?.currentNodeKey);
    const edge = pickChoiceEdge(edges, intent === 'existing' ? 'exist' : 'new', (edgeId) => {
      const target = outgoing.find((item) => item.id === edgeId)?.targetNodeKey;
      if (intent === 'existing') {
        return this.wfConfig.isSearchNode(target);
      }
      return this.wfConfig.isCreateNode(target);
    });
    const value = edge ? edgeConditionValue(edge) || uiStatus : uiStatus;
    const fieldCode =
      (this.wfConfig.isStatusNode(s?.currentNodeKey) === 'corporate'
        ? this.wfConfig.statusCorporate()?.fieldCode
        : this.wfConfig.statusIndividual()?.fieldCode) ?? 'applicantStatus';
    const canonicalStatus = normalizeStatusIntent(value);
    return this.store.resume({
      edgeId: edge?.id,
      output: {
        [fieldCode]: value,
        applicantStatus: canonicalStatus,
        statusOptions: value,
        customerStatus: value,
        ...(intent === 'new'
          ? { partyId: '', customerId: '', id: '', selectedRow: null }
          : {}),
      },
    });
  }

  /** Search form submit (existing customer) — advances to customer dataset. */
  async submitSearchForm(searchParams: Record<string, string>): Promise<RunState> {
    return this.store.resume({
      output: { ...searchParams, searchSubmitted: true },
    });
  }

  /**
   * From existing-customer results → reopen the search form modal on the list view.
   * Restarts the create-existing branch at the search pause node.
   */
  async returnToExistingCustomerSearchForm(
    applicantType: 'individual' | 'corporate',
  ): Promise<RunState> {
    await this.wfConfig.ensureLoaded();
    const key = await this.workflowKey();
    await this.store.start(key);
    await this.chooseApplicantType(applicantType);
    const state = await this.chooseCustomerStatus('existing');
    if (!this.isSearchFormPause()) {
      throw new Error('Workflow did not pause at existing customer search form');
    }
    return state;
  }

  /** Row selected on existing-customer-search → create form (existing). */
  async selectExistingCustomer(customer: {
    customerId: string;
    name: string;
    [key: string]: unknown;
  }): Promise<RunState> {
    await this.wfConfig.ensureLoaded();
    const s = this.store.state();
    const ctx = s?.contextSoFar ?? {};
    const applicantType = contextApplicantType(ctx);
    const datasetNodeKey = this.wfConfig.customerDatasetNodeKey(applicantType);
    const outgoing = this.wfConfig.outgoingEdges(s?.currentNodeKey);
    const edge =
      s?.availableEdges.find((runEdge) =>
        this.wfConfig.isCreateNode(outgoing.find((item) => item.id === runEdge.id)?.targetNodeKey),
      ) ??
      s?.availableEdges.find(
        (e) =>
          coerceText(e.condition?.value).toLowerCase().includes('create') ||
          e.label?.toLowerCase().includes('create'),
      );
    const output = this.wfConfig.buildCustomerDatasetActionOutput(
      datasetNodeKey,
      'action1',
      customer,
      ctx,
    );
    return this.store.resume({
      edgeId: edge?.id,
      output,
    });
  }

  /**
   * After OpportunitiesService.create succeeds — advance form → submit → calcs → list.
   * Submit executor skips remote POST when opportunityId is already present.
   */
  async afterCreateSaved(detail: {
    id: string;
    [key: string]: unknown;
  }): Promise<RunState> {
    const s = this.store.state();
    if (!s || s.status !== 'paused' || !this.isCreateFormPause()) {
      // Workflow not sitting on create form — just return to list and refresh run.
      await this.store.start(await this.workflowKey());
      return this.store.state()!;
    }
    return this.store.resume({
      output: {
        ...detail,
        opportunityId: detail.id,
        id: detail.id,
        formSubmitted: true,
      },
    });
  }

  /** Details / create cancel → return to list via workflow. */
  async backToList(): Promise<RunState> {
    const s = this.store.state();
    if (s?.status === 'paused' && this.isDetailPause()) {
      const edge =
        s.availableEdges.find((e) => e.id === 'e_detail_opportunity_back') ??
        s.availableEdges.find((e) => e.label?.toLowerCase().includes('back')) ??
        s.availableEdges[0];
      return this.store.resume({ edgeId: edge?.id, sourceHandle: 'back' });
    }
    // Create/search abandoned or other pause — re-enter at the list node.
    if (!this.isListPause()) {
      return this.store.start(await this.workflowKey());
    }
    return s!;
  }

  /** Switch internal shell view to match the current paused workflow node. */
  override async syncView(extraParams: Partial<OpportunityViewParams> = {}): Promise<void> {
    const s = this.store.state();
    if (!s) return;

    const current = this.nav.params();
    const mergedExtra: Partial<OpportunityViewParams> = {
      ...extraParams,
      detailTab: extraParams.detailTab ?? current.detailTab,
    };

    if (s.status === 'completed' || s.status === 'failed') {
      this.syncViewForTerminalRun(s, mergedExtra);
      return;
    }

    if (s.status !== 'paused' || !s.currentNodeKey) return;

    await this.wfConfig.ensureLoaded();

    if (this.trySyncViewEditIntent(s, mergedExtra)) {
      return;
    }

    const pausedNode = this.wfConfig.getNode(s.currentNodeKey);
    const resolution = this.wfConfig.shellViewForPausedNode(
      s.currentNodeKey,
      s.contextSoFar,
      mergedExtra,
    );

    logOpportunityNavTransition({
      stage: 'syncView resolution',
      pausedNodeKey: s.currentNodeKey,
      pausedNodeType: s.currentNodeType,
      pausedNodeLabel: pausedNode?.label ?? null,
      pausedNodeMode: this.wfConfig.formNodeMode(s.currentNodeKey),
      context: s.contextSoFar,
      resolutionView: resolution?.view ?? null,
      resolutionParams: resolution?.params,
    });

    if (!resolution) return;

    this.syncViewFromResolution(s, mergedExtra, resolution);
  }

  private syncViewForTerminalRun(
    run: RunState,
    mergedExtra: Partial<OpportunityViewParams>,
  ): void {
    const finalParams = { tab: mergedExtra.tab } as OpportunityViewParams;
    logOpportunityNavTransition({
      stage: 'syncView → list (terminal run)',
      context: run.contextSoFar,
      finalView: 'list',
      finalParams,
    });
    this.nav.replaceView('list', finalParams);
  }

  private resolveSyncViewEditNodeKey(
    mergedExtra: Partial<OpportunityViewParams>,
    currentNodeKey: string,
  ): string {
    const extraWfStep = mergedExtra.wfStep?.trim();
    if (extraWfStep && this.wfConfig.isEditFormPauseNode(extraWfStep)) {
      return extraWfStep;
    }
    if (this.wfConfig.isEditFormPauseNode(currentNodeKey)) {
      return currentNodeKey;
    }
    return extraWfStep ?? currentNodeKey ?? '';
  }

  private trySyncViewEditIntent(
    run: RunState,
    mergedExtra: Partial<OpportunityViewParams>,
  ): boolean {
    const editIntentParams = {
      ...mergedExtra,
      wfStep: mergedExtra.wfStep ?? run.currentNodeKey ?? '',
    } as OpportunityViewParams;
    if (!this.navHasEditIntent(editIntentParams) || !run.currentNodeKey) {
      return false;
    }

    const editNodeKey = this.resolveSyncViewEditNodeKey(mergedExtra, run.currentNodeKey);
    const editParams = buildEditFormViewParams(
      run.contextSoFar,
      this.wfConfig,
      editNodeKey,
      mergedExtra,
    );
    if (editParams) {
      logOpportunityNavTransition({
        stage: 'syncView → create (edit intent from restore/extra)',
        pausedNodeKey: run.currentNodeKey,
        context: run.contextSoFar,
        finalView: 'create',
        finalParams: editParams,
      });
      this.nav.replaceView('create', editParams);
      return true;
    }

    const fallbackId = coerceText(
      mergedExtra.id ?? mergedExtra.opportunityId ?? '',
    ).trim();
    if (!fallbackId || !editNodeKey) {
      return false;
    }

    const fallbackParams: OpportunityViewParams = {
      ...mergedExtra,
      id: fallbackId,
      opportunityId: fallbackId,
      wfStep: editNodeKey,
    };
    logOpportunityNavTransition({
      stage: 'syncView → create (edit intent fallback)',
      pausedNodeKey: run.currentNodeKey,
      context: run.contextSoFar,
      finalView: 'create',
      finalParams: fallbackParams,
    });
    this.nav.replaceView('create', fallbackParams);
    return true;
  }

  private syncViewFromResolution(
    run: RunState,
    mergedExtra: Partial<OpportunityViewParams>,
    resolution: NonNullable<ReturnType<OpportunityWorkflowConfigService['shellViewForPausedNode']>>,
  ): void {
    if (resolution.view === 'create') {
      this.syncViewCreateResolution(run, mergedExtra, resolution);
      return;
    }

    if (resolution.view === 'customer-search') {
      const searchParams = buildCustomerSearchViewParams(run.contextSoFar, resolution.params);
      logOpportunityNavTransition({
        stage: 'syncView → customer-search',
        pausedNodeKey: run.currentNodeKey,
        context: run.contextSoFar,
        resolutionView: resolution.view,
        finalView: 'customer-search',
        finalParams: searchParams,
      });
      this.nav.replaceView('customer-search', searchParams);
      return;
    }

    if (resolution.view === 'detail') {
      const detailParams =
        buildDetailViewParams(
          run.contextSoFar,
          this.wfConfig,
          resolution.params.wfStep ?? run.currentNodeKey ?? '',
          { ...mergedExtra, ...resolution.params },
        ) ?? (resolution.params as OpportunityViewParams);
      logOpportunityNavTransition({
        stage: 'syncView → detail',
        pausedNodeKey: run.currentNodeKey,
        context: run.contextSoFar,
        resolutionView: resolution.view,
        finalView: 'detail',
        finalParams: detailParams,
      });
      this.nav.replaceView('detail', detailParams);
      return;
    }

    const listParams = { ...mergedExtra, wfStep: resolution.params.wfStep } as OpportunityViewParams;
    logOpportunityNavTransition({
      stage: 'syncView → list',
      pausedNodeKey: run.currentNodeKey,
      context: run.contextSoFar,
      resolutionView: resolution.view,
      finalView: 'list',
      finalParams: listParams,
    });
    this.nav.replaceView('list', listParams);
  }

  private syncViewCreateResolution(
    run: RunState,
    mergedExtra: Partial<OpportunityViewParams>,
    resolution: { view: OpportunityView; params: Partial<OpportunityViewParams> },
  ): void {
    const pausedNodeKey = run.currentNodeKey ?? '';
    const pausedOnEditNode = this.wfConfig.isEditFormPauseNode(pausedNodeKey);
    const editIntentFromExtra = this.navHasEditIntent({
      ...mergedExtra,
      wfStep: mergedExtra.wfStep ?? pausedNodeKey,
    } as OpportunityViewParams);

    if (pausedOnEditNode || editIntentFromExtra) {
      const editNodeKey = pausedOnEditNode
        ? pausedNodeKey
        : (mergedExtra.wfStep?.trim() ?? pausedNodeKey);
      const editParams = buildEditFormViewParams(
        run.contextSoFar,
        this.wfConfig,
        editNodeKey,
        { ...mergedExtra, ...resolution.params },
      );
      if (editParams) {
        logOpportunityNavTransition({
          stage: 'syncView → create (edit mode)',
          pausedNodeKey: editNodeKey,
          context: run.contextSoFar,
          resolutionView: resolution.view,
          finalView: 'create',
          finalParams: editParams,
        });
        this.nav.replaceView('create', editParams);
        return;
      }

      const fallbackId = coerceText(
        resolution.params.id ??
          resolution.params.opportunityId ??
          mergedExtra.id ??
          mergedExtra.opportunityId ??
          '',
      ).trim();
      if (fallbackId) {
        const fallbackParams: OpportunityViewParams = {
          ...mergedExtra,
          ...resolution.params,
          id: fallbackId,
          opportunityId: fallbackId,
          wfStep: editNodeKey,
        };
        logOpportunityNavTransition({
          stage: 'syncView → create (edit mode fallback)',
          pausedNodeKey: editNodeKey,
          context: run.contextSoFar,
          resolutionView: resolution.view,
          finalView: 'create',
          finalParams: fallbackParams,
        });
        this.nav.replaceView('create', fallbackParams);
        return;
      }
    }

    const createParams = buildCreateFormViewParams(run.contextSoFar, {
      ...mergedExtra,
      ...resolution.params,
      wfStep: resolution.params.wfStep ?? run.currentNodeKey,
    });
    logOpportunityNavTransition({
      stage: 'syncView → create (new/existing party)',
      pausedNodeKey: run.currentNodeKey,
      context: run.contextSoFar,
      resolutionView: resolution.view,
      finalView: 'create',
      finalParams: createParams,
    });
    this.nav.replaceView('create', createParams);
  }

  /** @deprecated Use syncView — kept for callers migrating off router.navigate. */
  override async syncRoute(extraQuery: Record<string, string> = {}): Promise<void> {
    await this.syncView(viewParamsFromQuery(extraQuery));
  }

}
